import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from '../components/AppLayout';
import PageTitle from '../components/PageTitle';
import '../styles/pages/RegisterPage.css';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();

  useEffect(() => {
    document.title = '新規登録 | はしもと栄養パートナー';
  }, []);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showEmailVerification, setShowEmailVerification] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = () => {
    if (!formData.email || !formData.password || !formData.confirmPassword) {
      setError('すべての項目を入力してください');
      return false;
    }
    if (formData.password.length < 8) {
      setError('パスワードは8文字以上で設定してください');
      return false;
    }
    // 英字と数字の両方を含むかチェック
    const hasLetter = /[A-Za-z]/.test(formData.password);
    const hasNumber = /\d/.test(formData.password);
    if (!hasLetter || !hasNumber) {
      setError('パスワードは英字と数字の両方を含む必要があります');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('パスワードが一致しません');
      return false;
    }
    if (!termsAgreed) {
      setError('利用規約、プライバシーポリシー、医療免責事項に同意してください');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await register(formData.email, formData.password, 'free', termsAgreed);

      if (response?.requiresEmailVerification) {
        setShowEmailVerification(true);
      } else {
        // チャットページへ遷移（新規ユーザーフラグ付き）
        navigate('/chat?newUser=true');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.userMessage || err.response?.data?.message || err.message || '登録に失敗しました。もう一度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  // Show email verification success message
  if (showEmailVerification) {
    return (
      <AppLayout showMenu={false}>
        <PageTitle
          title="登録完了"
          subtitle="メールアドレスの確認"
          icon="✅"
        />

        <div className="tsumugi-container py-6">
          <div className="tsumugi-card">
            <div className="text-center">
              <div className="mb-4">
                <div className="register-page__success-icon-container">
                  <svg className="register-page__success-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>

              <h3 className="tsumugi-page-subtitle mb-4">会員登録が完了しました！</h3>

              <p className="mb-4 register-page__email-text">
                ご登録いただいたメールアドレス（<strong>{formData.email}</strong>）に確認メールを送信しました。
              </p>

              <p className="mb-6 register-page__email-text">
                メール内のリンクをクリックしてメールアドレスを確認してください。
              </p>

              <div className="tsumugi-info-box mb-4">
                <p>💡 メール認証前でも無料プランはご利用いただけます！</p>
              </div>

              <div className="tsumugi-warning-box mb-6">
                <h4 className="font-bold mb-2">⚠️ 重要：メールが届かない場合</h4>
                <div className="register-page__warning-highlight">
                  <p className="font-bold text-sm">
                    📧 <strong>迷惑メール・スパムフォルダ</strong>を必ずご確認ください！
                  </p>
                </div>
                <ul className="register-page__warning-list">
                  <li>Gmail・Yahoo!メールなどで迷惑メールに分類される可能性があります</li>
                  <li>メールアドレスの入力に間違いがないかご確認ください</li>
                  <li>数分お待ちいただいてからご確認ください</li>
                </ul>
              </div>

              <div className="register-page__button-group">
                <Link to="/chat?newUser=true" className="tsumugi-btn tsumugi-btn-primary">
                  つむぎと始める
                </Link>
                <Link to="/" className="tsumugi-btn">
                  ホームに戻る
                </Link>
              </div>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout showMenu={false}>
      <div className="text-center mb-6">
        <img
          src="/tsumugi.png"
          alt="つむぎ"
          className="mx-auto mb-4 register-page__mascot-img"
        />
        <h1 className="text-2xl font-bold text-text-main mb-2">はじめまして</h1>
        <p className="text-hint">つむぎと一緒に、無理なく始めましょう</p>
      </div>

      <div className="tsumugi-container py-6">
        <form onSubmit={handleSubmit}>
          <div className="tsumugi-card mb-4">
            {error && (
              <div className="tsumugi-error-box">
                <div className="tsumugi-error-box__icon">⚠️</div>
                <div className="tsumugi-error-box__text">{error}</div>
              </div>
            )}

            <div className="mb-4">
              <label htmlFor="email" className="tsumugi-form-label">
                メールアドレス
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="tsumugi-form-input"
                placeholder="your@email.com"
              />
            </div>

            <div className="mb-4">
              <label htmlFor="password" className="tsumugi-form-label">
                パスワード
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                value={formData.password}
                onChange={handleChange}
                className="tsumugi-form-input"
                placeholder="8文字以上（英字と数字を含む）"
              />
            </div>

            <div className="mb-4">
              <label htmlFor="confirmPassword" className="tsumugi-form-label">
                パスワード（確認）
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className="tsumugi-form-input"
                placeholder="パスワードを再入力"
              />
            </div>

            <div className="mb-4 p-3 register-page__password-requirements">
              <p className="register-page__password-title">
                🔒 パスワード要件
              </p>
              <ul className="register-page__password-list">
                <li>8文字以上で設定してください</li>
                <li>英字（A-Z, a-z）と数字（0-9）の両方を含めてください</li>
                <li>例: Password123, MyPass2024</li>
              </ul>
            </div>

            {/* 利用規約同意チェックボックス */}
            <div className="mb-4">
              <label
                className={`register-page__terms-container ${termsAgreed ? 'terms-agreed' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={termsAgreed}
                  onChange={(e) => setTermsAgreed(e.target.checked)}
                  className="register-page__terms-checkbox"
                  required
                />
                <span className="register-page__terms-text">
                  <Link to="/terms" target="_blank" className="register-page__terms-link">利用規約</Link>、
                  <Link to="/privacy" target="_blank" className="register-page__terms-link">プライバシーポリシー</Link>、
                  <Link to="/terms#medical-disclaimer" target="_blank" className="register-page__terms-link">医療免責事項</Link>
                  に同意します
                </span>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading || !termsAgreed}
              className={`tsumugi-btn tsumugi-btn-primary w-full ${!termsAgreed ? 'register-page__submit-btn--disabled' : 'register-page__submit-btn'}`}
            >
              {loading ? '登録中...' : '新規登録'}
            </button>

            <div className="register-page__login-link">
              すでにアカウントをお持ちの方は{' '}
              <Link to="/login" className="font-medium register-page__login-link-text">
                ログイン
              </Link>
            </div>
          </div>
        </form>

        <div className="tsumugi-card text-center">
          <h4 className="register-page__how-to-use-title">初めてご利用の方</h4>
          <Link
            to="/how-to-use"
            className="inline-flex items-center register-page__how-to-use-link"
          >
            <span className="mr-2">📖</span>
            使い方ガイドを見る
          </Link>
        </div>

      </div>
    </AppLayout>
  );
};

export default RegisterPage;
